import { useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router';
import { useMaterialReactTable, MaterialReactTable } from 'material-react-table';
import { Box, Button } from '@mui/material';
import { Edit } from '@mui/icons-material';
import { createAlertAC } from '../../redux/actions/dataAlerts';
import { unique } from '../../utils/collections';
import EquipmentLoader from '../loaders/EquipmentLoader';
import AlertCreationDialog from './AlertCreationDialog';
import { getBuildingsEntities } from '../../redux/selectors/buildings';
import {
  tableSelectionOptions,
  defaultTableOptions,
  defaultTableInitialState,
  LIST_DELIMITER,
  MISSING_VALUE,
  prepareUrlColumnFilters,
} from '../util/data-table';
import { determineStatus, ONLINE, OFFLINE, ALERT, statusColor, statusLink } from './explore';
import { DateCell, PercentageCell, TooltipHeader, CellList } from '../util/DataTableCells';
import { buildStagingLink } from '../staging/staging';
import { equipmentIdId } from '../staging/columns';

const EquipmentTable = ({
  isLoading,
  points: pointMap,
  equipment,
  buildingsById,
  sorting,
  onSortingChange,
  columnFilters,
  onColumnFiltersChange,
  equipmentSelection,
  onRowSelectionChange,
  equipTypesById,
  createAlert,
  navigate,
  alerts,
  latestHeartbeatByBid,
}) => {
  const [newAlert, setNewAlert] = useState([]);
  const now = useMemo(() => new Date(), []);
  const data = useMemo(
    () =>
      equipment
        .values()
        .map((e) => ({
          building: buildingsById[e.buildingId],
          status: determineStatus(
            now,
            latestHeartbeatByBid[e.building_id],
            alerts,
            e.building_id,
            e.id
          ),
          ...e,
        }))
        .toArray(),
    [equipment, buildingsById, alerts, now, latestHeartbeatByBid]
  );

  const observedEquipTypeIds = useMemo(
    () => unique(data.map(({ equipmentTypeId }) => equipmentTypeId)),
    [data]
  );

  const columns = useMemo(() => {
    const lastHour = new Date() - 1 * 60 * 60 * 1000;
    const equipTypeOptions = Object.entries(equipTypesById)
      .map(([value, et]) => ({
        label: et.nameAbbr,
        value: Number.parseInt(value, 10),
      }))
      .filter(({ value }) => observedEquipTypeIds.includes(value));
    const liveHeader = ({ column }) => (
      <TooltipHeader
        tooltip="Live points have been updated in the past hour."
        content={column.columnDef.header}
      />
    );
    const liveHeaderPercent = ({ column }) => (
      <TooltipHeader tooltip="Values are in percentiles" content={column.columnDef.header} />
    );
    const equipType = ({ row, renderedCellValue }) => (
      <>
        {equipTypesById[renderedCellValue]?.nameAbbr ?? renderedCellValue}
        <Link
          to={buildStagingLink(row.original.buildingId, 'equipment', {
            [equipmentIdId]: row.original.equipId,
          })}
        >
          <Edit color="action" />
        </Link>
      </>
    );
    const equipName = ({ row, renderedCellValue }) => (
      <>
        {renderedCellValue}
        <Link
          to={buildStagingLink(row.original.buildingId, 'equipment', {
            [equipmentIdId]: row.original.equipId,
          })}
        >
          <Edit color="action" />
        </Link>
      </>
    );
    return [
      // TODO: group shared column defs in shared file
      {
        id: 'b_name',
        header: 'Building Name',
        accessorFn: ({ building }) => building?.name,
        filterVariant: 'autocomplete',
      },
      {
        id: 'e_name',
        accessorKey: 'name',
        header: 'Equipment Name',
        filterVariant: 'autocomplete',
        Cell: equipName,
      },
      {
        id: 'e_type',
        accessorFn: (e) => [e?.equipmentTypeId],
        header: 'Equipment Type',
        filterVariant: 'multi-select',
        filterSelectOptions: equipTypeOptions,
        Cell: equipType,
      },
      {
        id: 'e_livePointCount',
        header: 'Live Points (count)',
        Header: liveHeader,
        filterVariant: 'range',
        accessorFn: ({ points }) => points?.filter((p) => p.lastUpdatedTs >= lastHour).length ?? 0,
      },
      {
        id: 'e_pointCount',
        header: 'Total Points (count)',
        filterVariant: 'range',
        accessorFn: ({ points }) => points?.length ?? 0,
      },
      {
        id: 'e_livePointPercent',
        filterVariant: 'range',
        header: 'Live Points (percent)',
        Header: liveHeaderPercent,
        // Hacking in -1 for NaNs to get sorting the way we want
        accessorFn: ({ points }) => {
          const count = points?.length ?? 0;
          const liveCount = points?.filter((p) => p.lastUpdatedTs >= lastHour).length ?? 0;
          const result = liveCount / count;
          return Number.isNaN(result) ? -1 : result * 100;
        },
        Cell: PercentageCell,
      },
      {
        id: 'e_lastIngest',
        header: 'Last Data Ingested',
        sortingFn: 'datetime',
        enableColumnFilter: false,
        // hijacking the min time to seperate no ingests case from no published points case
        accessorFn: ({ points }) => {
          const updates = points?.map((p) => p?.lastUpdatedTs).filter(Boolean);
          return updates.length <= 0 ? MISSING_VALUE : new Date(Math.max(...updates));
        },

        Cell: DateCell,
      },
      {
        id: 'e_feeds',
        accessorFn: (e) =>
          e.feedsEquipment.map((eid) => equipment.get(eid)?.name).join(LIST_DELIMITER),
        header: 'FEEDS',
        Cell: CellList,
      },
      {
        id: 'e_fed',
        accessorFn: (e) =>
          e.fedByEquipment.map((eid) => equipment.get(eid)?.name).join(LIST_DELIMITER),
        header: 'FED BY',
        Cell: CellList,
      },
      {
        id: 'e_contained',
        accessorFn: (e) => e.containedByLocation || MISSING_VALUE,
        header: 'CONTAINED BY',
      },
      {
        id: 'e_device_ids',
        header: 'Device Ids',
        accessorFn: ({ points }) =>
          unique(points?.map((p) => p.device?.device_id))
            ?.toSorted()
            ?.join(LIST_DELIMITER),
      },
      {
        id: 'e_device_names',
        header: 'Device Names',
        accessorFn: ({ points }) =>
          unique(points?.map((p) => p.device?.name))
            ?.toSorted()
            ?.join(LIST_DELIMITER),
      },
      {
        id: 'e_tags',
        header: 'Haystack Tags',
        accessorFn: ({ tags }) => tags?.toSorted().join(LIST_DELIMITER),
      },
      {
        id: 'e_labels',
        header: 'Custom Labels',
        accessorFn: ({ labels }) => labels?.toSorted().join(LIST_DELIMITER),
      },
      {
        id: 'e_status',
        accessorKey: 'status',
        header: 'Status',
        filterVariant: 'select',
        filterSelectOptions: [ALERT, ONLINE, OFFLINE],
        // eslint-disable-next-line react/no-unstable-nested-components
        Cell: ({ row, renderedCellValue }) =>
          renderedCellValue ? (
            <Link to={statusLink(renderedCellValue, row)}>
              <Button variant="contained" color={statusColor(renderedCellValue)}>
                {renderedCellValue}
              </Button>
            </Link>
          ) : null,
      },
    ];
  }, [equipment, equipTypesById, observedEquipTypeIds]);

  const showBuildings = Object.keys(buildingsById).length > 1;

  const columnIds = useMemo(() => columns.map(({ id }) => id), [columns]);
  const equipSorting = useMemo(
    () => sorting.filter(({ id }) => columnIds.includes(id)),
    [sorting, columnIds]
  );
  const equipColumnFilters = useMemo(
    () => columnFilters.filter(({ id }) => columnIds.includes(id)),
    [columnFilters, columnIds]
  );

  const mrt = useMaterialReactTable({
    ...defaultTableOptions,
    ...tableSelectionOptions,
    // https://onboard-data.slack.com/archives/C01JX53PBJ5/p1780328209112699
    enableGlobalFilter: false,
    columns,
    data,
    getRowId: (row) => row.id,
    initialState: {
      ...defaultTableInitialState({
        columnFilters: equipColumnFilters,
        sorting: equipSorting,
        hiddenColumns: [
          showBuildings ? null : 'b_name',
          'e_feeds',
          'e_fed',
          'e_contained',
          'e_device_ids',
          'e_device_names',
          'e_tags',
          'e_labels',
        ],
      }),
    },
    onRowSelectionChange,
    // sorting
    onSortingChange,
    // filtering
    onColumnFiltersChange,
    state: {
      sorting: equipSorting,
      columnFilters: equipColumnFilters,
      rowSelection: equipmentSelection,
      showAlertBanner: true,
      isLoading,
    },
    renderTopToolbarCustomActions: ({ table }) => (
      <Box sx={{ display: 'flex', gap: '0.5rem' }}>
        <Button
          color="success"
          disabled={table.getSelectedRowModel().flatRows.length <= 0}
          onClick={() =>
            setNewAlert(table.getSelectedRowModel().flatRows.map(({ original }) => original.id))
          }
          variant="contained"
        >
          Create Alert
        </Button>
      </Box>
    ),
  });

  if (Object.keys(buildingsById).length <= 0) return 'Too few buildings';
  if (Object.keys(buildingsById).length >= 10) return 'Too many buildings';
  return (
    <div>
      {Object.keys(buildingsById).map((bid) => (
        <EquipmentLoader key={bid} buildingId={bid} getDevices />
      ))}
      <AlertCreationDialog
        onSubmit={(a) =>
          createAlert(a).then(() =>
            navigate(`/data-alerts?filterBy=${prepareUrlColumnFilters({ name: a.name })}`)
          )
        }
        onCancel={() => setNewAlert([])}
        equipmentIds={newAlert}
        points={pointMap}
      />
      <MaterialReactTable table={mrt} />
    </div>
  );
};

const mapStateToProps = (state, { buildingsById }) => {
  const {
    entities: {
      dataAlerts: { alerts },
      deployments: { deployments },
    },
    ui: { buildingsLoadingData },
  } = state;
  const latestHeartbeatByBid = Object.fromEntries(
    Map.groupBy(
      Object.values(deployments).flatMap((d) =>
        d?.buildings?.map((bid) => [bid, d.last_heartbeat])
      ),
      (e) => e[0]
    )
      .entries()
      .map((entry) => [entry[0], Math.max(0, ...entry[1].map((e) => e[1]))])
  );
  return {
    isLoading: Object.keys(buildingsById).some((bid) => buildingsLoadingData.has(bid)),
    ...getBuildingsEntities(state, Object.keys(buildingsById)),
    buildingsById,
    equipTypesById: state.entities.equipTypes.byId || {},
    alerts,
    latestHeartbeatByBid,
  };
};

const mapDispatchToProps = (dispatch) => ({
  createAlert: (alert) => dispatch(createAlertAC(alert)),
});

export default connect(mapStateToProps, mapDispatchToProps)(EquipmentTable);
